import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchCronJobs, deleteCronJobs, updateCronJobsStatus } from '../redux/slices/cronSlice';
import { useNavigate, Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Spinner from '../components/Spinner';
import { FaSort, FaSortUp, FaSortDown } from 'react-icons/fa';

const CronJobList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { cronJobs, loading } = useSelector((state) => state.cron);
  const [selectedCronJobs, setSelectedCronJobs] = useState([]);
  const [isAllSelected, setIsAllSelected] = useState(false);
  const [searchQuery, setSearchQuery] = useState(() => {
    return localStorage.getItem('cronJobSearchQuery') || '';
  });

  useEffect(() => {
    localStorage.setItem('cronJobSearchQuery', searchQuery);
  }, [searchQuery]);

  const [actionLoading, setActionLoading] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'asc' });

  useEffect(() => {
    dispatch(fetchCronJobs());
  }, [dispatch]);

  const filteredCronJobs = cronJobs.filter((cronJob) => {
    const name = cronJob.name?.toLowerCase() || '';
    const description = cronJob.description?.toLowerCase() || '';
    const query = searchQuery.toLowerCase();
    return name.includes(query) || description.includes(query);
  });

  const sortedCronJobs = [...filteredCronJobs].sort((a, b) => {
    if (a[sortConfig.key] < b[sortConfig.key]) {
      return sortConfig.direction === 'asc' ? -1 : 1;
    }
    if (a[sortConfig.key] > b[sortConfig.key]) {
      return sortConfig.direction === 'asc' ? 1 : -1;
    }
    return 0;
  });

  useEffect(() => {
    setIsAllSelected(selectedCronJobs.length === filteredCronJobs.length);
  }, [selectedCronJobs, filteredCronJobs]);

  const handleSelect = (id) => {
    setSelectedCronJobs((prev) =>
      prev.includes(id) ? prev.filter((cronId) => cronId !== id) : [...prev, id]
    );
  };

  const handleSelectAll = () => {
    setSelectedCronJobs(isAllSelected ? [] : filteredCronJobs.map((cronJob) => cronJob.id));
  };

  const handleDelete = async () => {
    if (selectedCronJobs.length) {
      setActionLoading(true);
      await dispatch(deleteCronJobs(selectedCronJobs));
      setActionLoading(false);
      setSelectedCronJobs([]);
    }
  };

  const handleStatusToggle = async (status) => {
    if (selectedCronJobs.length) {
      setActionLoading(true);
      await dispatch(updateCronJobsStatus({ ids: selectedCronJobs, status }));
      setActionLoading(false);
      setSelectedCronJobs([]);
    }
  };

  const handleCreateNewCronJob = () => {
    navigate('/create');
  };

  const handleEditCronJob = (e, id) => {
    if (e.type === 'auxclick' && e.button !== 1) return;
    if (e.ctrlKey || e.metaKey || e.button === 1) {
      window.open(`/edit/${id}`, '_blank');
    } else {
      navigate(`/edit/${id}`);
    }
  };

  const handleCheckboxClick = (event) => {
    event.stopPropagation();
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  return (
    <div className="w-full flex justify-center">
      <div className="w-[95%]">
        <Navbar
          title="Cron Job Scheduler"
          subtitle="Schedule And Automate Tasks With Ease."
          buttonLabel="Schedule New Task"
          buttonAction={handleCreateNewCronJob}
        />

        {loading || actionLoading ? (
          <div className="flex justify-center items-center mt-10">
            <Spinner />
          </div>
        ) : (
          <div>
            <div className="sticky top-24 bg-white z-10 flex items-center justify-between p-2 border border-gray-200 rounded-lg shadow mt-6">
              <div className="flex space-x-2">
                <button
                  onClick={() => handleStatusToggle('active')}
                  disabled={!selectedCronJobs.length}
                  className={`px-4 py-2 border rounded text-sm font-medium ${selectedCronJobs.length
                    ? 'bg-purple-600 text-white hover:bg-purple-700'
                    : 'text-gray-600 cursor-not-allowed'
                    }`}
                >
                  Enable Selected
                </button>
                <button
                  onClick={() => handleStatusToggle('inactive')}
                  disabled={!selectedCronJobs.length}
                  className={`px-4 py-2 border rounded text-sm font-medium ${selectedCronJobs.length
                    ? 'bg-gray-500 text-white hover:bg-gray-600'
                    : 'text-gray-600 cursor-not-allowed'
                    }`}
                >
                  Disable Selected
                </button>
                <button
                  onClick={handleDelete}
                  disabled={!selectedCronJobs.length}
                  className={`px-4 py-2 border rounded text-sm font-medium ${selectedCronJobs.length
                    ? 'bg-red-500 text-white hover:bg-red-600'
                    : 'text-gray-600 cursor-not-allowed'
                    }`}
                >
                  Delete Selected
                </button>
              </div>

              <div className='flex gap-4'>
                <input
                  type="text"
                  placeholder="Search Here"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="border border-gray-300 px-4 py-2 rounded text-sm focus:outline-none"
                />
              </div>
            </div>

            <div className="bg-white shadow-md p-4 rounded-md mt-20">
              <div className="overflow-y-auto max-h-[400px]">
                <table className="min-w-full bg-white text-sm text-left">
                  <thead className="bg-purple-300 text-black sticky top-0">
                    <tr>
                      <th className="px-4 py-3 text-center">
                        <input
                          type="checkbox"
                          checked={isAllSelected}
                          onChange={handleSelectAll}
                          className="w-5 h-5"
                        />
                      </th>
                      <th className="px-4 py-3">User</th>
                      <th className="px-4 py-3 cursor-pointer" onClick={() => handleSort('name')}>
                        Name
                        {sortConfig.key === 'name' ? (
                          sortConfig.direction === 'asc' ? (
                            <FaSortUp className="inline ml-1" />
                          ) : (
                            <FaSortDown className="inline ml-1" />
                          )
                        ) : (
                          <FaSort className="inline ml-1" />
                        )}
                      </th>
                      <th className="px-4 py-3 cursor-pointer" onClick={() => handleSort('description')}>
                        Description
                        {sortConfig.key === 'description' ? (
                          sortConfig.direction === 'asc' ? (
                            <FaSortUp className="inline ml-1" />
                          ) : (
                            <FaSortDown className="inline ml-1" />
                          )
                        ) : (
                          <FaSort className="inline ml-1" />
                        )}
                      </th>
                      <th className="px-4 py-3 text-center cursor-pointer" onClick={() => handleSort('status')}>
                        Status
                        {sortConfig.key === 'status' ? (
                          sortConfig.direction === 'asc' ? (
                            <FaSortUp className="inline ml-1" />
                          ) : (
                            <FaSortDown className="inline ml-1" />
                          )
                        ) : (
                          <FaSort className="inline ml-1" />
                        )}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedCronJobs.map((cronJob) => (
                      <tr
                        key={cronJob.id}
                        className="hover:bg-gray-50 border-b cursor-pointer"
                        onClick={(e) => handleEditCronJob(e, cronJob.id)}
                        onAuxClick={(e) => handleEditCronJob(e, cronJob.id)}
                      >
                        <td className="px-4 py-3 text-center">
                          <input
                            type="checkbox"
                            checked={selectedCronJobs.includes(cronJob.id)}
                            onChange={() => handleSelect(cronJob.id)}
                            onClick={handleCheckboxClick}
                            className="w-5 h-5"
                          />
                        </td>
                        <td className="px-4 py-3">Trayistats</td>
                        <td className="px-4 py-3">
                          <Link
                            to={`/edit/${cronJob.id}`}
                            className="hover:text-purple-600 hover:underline"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {cronJob.name || 'Unnamed'}
                          </Link>
                        </td>
                        <td className="px-4 py-3">{cronJob.description || 'No Description'}</td>
                        <td className="px-4 py-3 text-center">
                          <span
                            className={`px-2 py-1 text-xs font-medium rounded ${cronJob.status === 'active'
                              ? 'bg-green-100 text-green-600'
                              : 'bg-red-100 text-red-600'
                              }`}
                          >
                            {cronJob.status
                              ? cronJob.status.charAt(0).toUpperCase() + cronJob.status.slice(1)
                              : 'Unknown'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CronJobList;